/* Presentation only: Shopify-rendered data and the shared engine own offer eligibility. */
(() => {
  if (customElements.get('wolf-set-offer')) return;

  const states = new Set(['progress', 'qualified', 'success', 'hint', 'shipping-progress', 'shipping-qualified']);
  const text = (value) => typeof value === 'string' ? value.trim() : '';
  // Shopify replaces the cart sections after every change. Keep only the last
  // painted fraction per surface so the replacement can continue from it.
  const progressHistory = new Map();
  const motionDuration = 520;
  const clamp = (value) => Math.max(0, Math.min(1, value));
  let disclosureId = 0;

  function node(tag, className, content) {
    const element = document.createElement(tag);
    element.className = className;
    if (content) element.textContent = content;
    return element;
  }

  function destination(href) {
    if (typeof href !== 'string' || !href.trim()) return null;
    try {
      const url = new URL(href, window.location.href);
      return ['http:', 'https:'].includes(url.protocol) && url.origin === window.location.origin ? url.href : null;
    } catch (_) {
      return null;
    }
  }

  function checkmark() {
    const namespace = 'http://www.w3.org/2000/svg';
    const icon = document.createElementNS(namespace, 'svg');
    icon.setAttribute('class', 'wolf-set-offer__check');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '1.2');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    const circle = document.createElementNS(namespace, 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '10.5');
    const tick = document.createElementNS(namespace, 'path');
    tick.setAttribute('d', 'm8.7 12.1 2.2 2.2 4.4-4.4');
    tick.setAttribute('stroke-linecap', 'round');
    tick.setAttribute('stroke-linejoin', 'round');
    icon.append(circle, tick);
    return icon;
  }

  class WolfSetOffer extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.hidden = true;
      this.querySelector('[data-wolf-offer-content]')?.remove();
      this.removeAttribute('data-state');
      this.removeAttribute('data-expanded');
      const drawer = this.closest('cart-drawer');
      this.surface = drawer ? 'drawer' : 'cart';
      try {
        const data = JSON.parse(this.querySelector('[data-wolf-offer-data]')?.textContent || 'null');
        if (data?.context === 'product') this.surface = 'product';
        // Both messages can coexist. A hidden or refreshed quantity offer must
        // not reset the shipping bar's independent animation history.
        this.surface += `:${data?.channel || 'all'}`;
        if (!data || !window.WolfSetOfferEngine) {
          progressHistory.delete(this.surface);
          return;
        }
        const result = window.WolfSetOfferEngine.evaluate(data);
        if (!result || !states.has(result.state) || !text(result.message)) {
          progressHistory.delete(this.surface);
          return;
        }

        const success = result.state === 'success' || result.state === 'shipping-qualified';
        const shipping = result.state.startsWith('shipping-');
        const card = node('div', 'wolf-set-offer__card');
        card.setAttribute('data-wolf-offer-content', '');
        const heading = text(result.heading);
        this.setAttribute('role', 'region');
        this.setAttribute('aria-label', heading || (success ? 'Offer applied' : 'Complete your set'));

        if (heading && result.state !== 'hint' && result.state !== 'success' && !shipping) {
          card.append(node('h3', 'wolf-set-offer__heading', heading));
        }

        const message = node('p', 'wolf-set-offer__message');
        // The inactive drawer shares the document with the full cart. Let the
        // visible cart and its existing live region announce updates only once.
        if (data.context === 'cart' && (!drawer || drawer.classList.contains('active'))) {
          message.setAttribute('role', 'status');
          message.setAttribute('aria-live', 'polite');
          message.setAttribute('aria-atomic', 'true');
        }
        if (success) message.append(checkmark());
        const messageText = result.state === 'success' ? `${heading}. ${text(result.message)}` : text(result.message);
        message.append(node(success || result.state === 'hint' ? 'strong' : 'span', 'wolf-set-offer__message-text', messageText));
        card.append(message);

        if (text(result.supportingText) && result.state !== 'success' && !shipping) {
          card.append(node('p', 'wolf-set-offer__supporting', text(result.supportingText)));
        }

        const total = Number(result.progress?.total);
        const current = Number(result.progress?.current);
        let animation;
        if (result.progress && Number.isFinite(total) && total > 0 && Number.isFinite(current) && current >= 0) {
          const value = Math.min(current, total);
          const continuous = result.state.startsWith('shipping-');
          const count = continuous ? 1 : Math.min(100, Math.max(1, Math.ceil(total)));
          const progress = node('div', 'wolf-set-offer__progress');
          progress.dataset.mode = continuous ? 'continuous' : 'segmented';
          progress.style.setProperty('--wolf-progress-gaps', String(Math.max(1, count - 1)));
          progress.setAttribute('role', 'progressbar');
          progress.setAttribute('aria-label', heading || 'Offer progress');
          progress.setAttribute('aria-valuemin', '0');
          progress.setAttribute('aria-valuemax', String(total));
          progress.setAttribute('aria-valuenow', String(value));
          progress.setAttribute('aria-valuetext', text(result.message));
          const fills = [];
          for (let index = 0; index < count; index += 1) {
            const segment = node('span', 'wolf-set-offer__segment');
            segment.setAttribute('aria-hidden', 'true');
            const fill = node('span', 'wolf-set-offer__segment-fill');
            fills.push(fill);
            segment.append(fill);
            progress.append(segment);
          }
          card.append(progress);
          animation = {
            fills, target: value / total,
            signature: JSON.stringify([result.id, progress.dataset.mode, total, data.cart?.currency]),
          };
        }

        const href = destination(result.link?.href);
        if (!shipping && href && text(result.link?.label)) {
          const linkClass = data.context === 'cart'
            ? 'wolf-set-offer__link button button--secondary wolf-cart-button'
            : 'wolf-set-offer__link';
          const link = node('a', linkClass, text(result.link.label));
          link.href = href;
          if (data.context === 'cart' && card.querySelector('.wolf-set-offer__heading')) {
            this.setupDisclosure(card, link);
          } else {
            card.append(link);
          }
        }

        this.dataset.state = result.state;
        this.append(card);
        this.hidden = false;
        if (animation) this.animateProgress(animation);
        else progressHistory.delete(this.surface);
      } catch (_) {
        // Optional guidance must never interrupt purchasing or retain a stale promise.
        this.disconnectedCallback();
        progressHistory.delete(this.surface);
        this.hidden = true;
      }
    }

    setupDisclosure(card, link) {
      const heading = card.querySelector('.wolf-set-offer__heading');
      const toggle = node('button', 'wolf-set-offer__toggle');
      toggle.type = 'button';
      toggle.append(node('span', 'wolf-set-offer__title', heading.textContent));
      const caret = node('span', 'wolf-set-offer__caret');
      caret.setAttribute('aria-hidden', 'true');
      toggle.append(caret);
      heading.textContent = '';
      heading.append(toggle);

      const actions = node('div', 'wolf-set-offer__actions');
      actions.id = `WolfOfferActions-${++disclosureId}`;
      const clip = node('div', 'wolf-set-offer__actions-clip');
      const content = node('div', 'wolf-set-offer__actions-content');
      content.append(link);
      clip.append(content);
      actions.append(clip);
      card.append(actions);
      toggle.setAttribute('aria-controls', actions.id);

      this.events = new AbortController();
      const options = { signal: this.events.signal };
      let hovered = false;
      let expanded = false;
      let pointerDownInside = false;
      const setExpanded = (open) => {
        expanded = Boolean(open);
        this.toggleAttribute('data-expanded', expanded);
        toggle.setAttribute('aria-expanded', String(expanded));
        actions.setAttribute('aria-hidden', String(!expanded));
        actions.inert = !expanded;
      };
      const keyboardFocusWithin = () => card.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
      card.addEventListener('pointerdown', (event) => {
        pointerDownInside = true;
        // A hybrid device can switch to touch without sending mouseleave.
        if (event.pointerType !== 'mouse') hovered = false;
      }, options);
      card.addEventListener('pointercancel', () => { pointerDownInside = false; }, options);
      card.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        hovered = true;
        setExpanded(true);
      }, options);
      card.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        hovered = false;
        pointerDownInside = false;
        setExpanded(keyboardFocusWithin());
      }, options);
      toggle.addEventListener('click', (event) => {
        // Hover handles a mouse; the same heading is a disclosure on touch or keyboard.
        if (event.detail > 0 && hovered) return;
        setExpanded(!expanded);
      }, options);
      card.addEventListener('click', (event) => {
        pointerDownInside = false;
        // Copy and progress are also tap targets; links and controls keep their native action.
        if (event.target.closest('a, button, input, select, textarea, summary')) return;
        if (event.detail > 0 && hovered) return;
        setExpanded(!expanded);
      }, options);
      card.addEventListener('focusout', (event) => {
        // Some touch browsers blur the heading before an internal tap becomes a click.
        if (event.relatedTarget === null && pointerDownInside) return;
        if (!card.contains(event.relatedTarget)) {
          pointerDownInside = false;
          setExpanded(hovered);
        }
      }, options);
      card.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape' || !expanded) return;
        event.stopPropagation();
        if (actions.contains(document.activeElement)) toggle.focus();
        hovered = false;
        setExpanded(false);
      }, options);
      setExpanded(false);
    }

    animateProgress({ fills, target, signature }) {
      const previous = progressHistory.get(this.surface);
      const from = previous?.signature === signature ? previous.value : 0;
      const record = { signature, value: from };
      progressHistory.set(this.surface, record);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const paint = (value) => {
        record.value = value;
        // Animate one overall amount, so multi-item changes sweep across the
        // segments in order instead of filling every segment simultaneously.
        fills.forEach((fill, index) => {
          fill.style.transform = `scaleX(${clamp(value * fills.length - index)})`;
        });
      };
      if (reducedMotion.matches || from === target) {
        paint(target);
        return;
      }
      paint(from);
      const startedAt = performance.now();
      const tick = (now) => {
        this.animationFrame = null;
        if (!this.isConnected || progressHistory.get(this.surface) !== record) return;
        const elapsed = reducedMotion.matches ? 1 : clamp((now - startedAt) / motionDuration);
        const eased = 1 - Math.pow(1 - elapsed, 3);
        paint(elapsed === 1 ? target : from + (target - from) * eased);
        if (elapsed < 1) this.animationFrame = requestAnimationFrame(tick);
      };
      this.animationFrame = requestAnimationFrame(tick);
    }

    disconnectedCallback() {
      this.events?.abort();
      if (this.animationFrame != null) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  customElements.define('wolf-set-offer', WolfSetOffer);
})();
